import _ from 'lodash-es'
import gsap from 'gsap'
import {
  CHALLENGES_TITLES,
  CHALLENGES_COLORS,
  Challenge,
  CHALLENGES_ICONS_PATHS_16PX,
} from './constants'
import {
  roundedRect,
  computeTextWidth,
  saveRestore,
  computeAlignOffset,
  Align,
} from './canvas-utils'
import { isBetween } from './math-utils'
import { formatPercentageUniversal } from './format-utils'
import { Point } from './types'

const DURATION = 0.25
const challengeFont = (fontSize: number) => `normal ${fontSize}px 'rational'`
const percentageFont = (fontSize: number) => `bold ${fontSize}px 'rational'`
const formatLabelPercentage = (percentage: number) =>
  formatPercentageUniversal(percentage, {
    significant: percentage >= 10 ? 2 : 1,
  })

const padding = { top: 2, right: 2, bottom: 2, left: 2 }
const hSpacing = 4
const iconDimension = 16
const boxHeight = padding.top + iconDimension + padding.bottom

interface DrawLabelOptions {
  x: number
  y: number
  opacity?: number
  align?: Align
  challenge: Challenge
  percentage?: number
  showChallenge?: boolean
  showPercentage?: boolean
  showShadow?: boolean
  fontSize?: number
  postfix?: string
}

const defaultOptions = <T extends DrawLabelOptions>(opts: T): T & Required<DrawLabelOptions> => ({
  opacity: 1,
  align: 'bottom left',
  showChallenge: true,
  showPercentage: true,
  showShadow: true,
  percentage: 0,
  fontSize: 12,
  postfix: '',
  ...opts,
})

interface LabelTween {
  challengePerc?: number
  percentagePerc?: number
}

const defaultTween = (tween: LabelTween, opts: Required<DrawLabelOptions>) => ({
  challengePerc: opts.showChallenge ? 1 : 0,
  percentagePerc: opts.showPercentage ? 1 : 0,
  ...tween,
})

export function drawChallengeLabel(
  ctx: CanvasRenderingContext2D,
  options: DrawLabelOptions,
  tween: LabelTween = {}
) {
  const defOptions = defaultOptions(options)
  const defTween = defaultTween(tween, defOptions)

  const { challenge, postfix, opacity, percentage, showShadow, fontSize } = defOptions
  const { challengePerc, percentagePerc } = defTween

  const iconPath = CHALLENGES_ICONS_PATHS_16PX[challenge]
  const iconColor = CHALLENGES_COLORS[challenge]

  const percentageText = formatLabelPercentage(percentage) + postfix

  const { top, left, width, height } = computeChallengeLabelMetrics(ctx, defOptions, defTween)

  saveRestore(ctx, () => {
    ctx.globalAlpha = opacity
    ctx.translate(left, top + height)

    // ctx.save()
    // ctx.strokeStyle = 'green'
    // ctx.stroke(new Path2D('M0,-50 L0,50 M-50,0 L50,0'))
    // ctx.restore()

    // RECT
    if (showShadow) {
      saveRestore(ctx, () => {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
        ctx.shadowBlur = 4
        roundedRect(ctx, {
          x: 0,
          y: -height,
          width: width,
          height: height,
          radius: 3,
          color: 'white',
        })
      })
    }

    // ICON
    saveRestore(ctx, () => {
      ctx.translate(padding.left, -(padding.bottom + iconDimension))
      ctx.shadowColor = 'transparent'
      ctx.fillStyle = iconColor
      const icon = new Path2D(iconPath)
      ctx.fill(icon)
    })

    // TEXT
    if (challengePerc) {
      saveRestore(ctx, () => {
        ctx.globalAlpha = Math.max(0, challengePerc * 4 - 3) * opacity
        ctx.fillStyle = 'black'
        ctx.font = challengeFont(fontSize)
        ctx.textAlign = 'left'
        const challengeText = CHALLENGES_TITLES[challenge]
        ctx.fillText(challengeText, padding.left + iconDimension + hSpacing, -5)
      })
    }

    if (percentagePerc) {
      saveRestore(ctx, () => {
        ctx.globalAlpha = Math.max(0, percentagePerc * 4 - 3) * opacity
        ctx.fillStyle = 'black'
        ctx.font = percentageFont(fontSize)
        ctx.textAlign = 'right'
        ctx.fillText(percentageText, width - padding.right, -5)
        ctx.globalAlpha = 1
      })
    }
  })

  return { y: top, x: left, width, height }
}

interface LabelOptions extends DrawLabelOptions {
  startLoop?: () => void
  stopLoop?: () => void
}

interface AnimationOption {
  animated?: boolean
}

const defaultAnimation = (anim: AnimationOption): Required<AnimationOption> => ({
  animated: true,
  ...anim,
})

export class ChallengeLabel {
  labelTween: Required<LabelTween>
  labelOptions: Required<LabelOptions>

  constructor(public ctx: CanvasRenderingContext2D, opts: LabelOptions) {
    const defaulted: Required<LabelOptions> = {
      startLoop: () => {},
      stopLoop: () => {},
      ...defaultOptions(opts),
    }

    this.labelOptions = defaulted
    this.labelTween = defaultTween({}, defaulted)
  }

  /**
   * Update label state (no tweening). Passing a value for showChallenge and
   * showPercentage is not allowed. Call setShowChallenge and setShowPercentage instead
   */
  update(opts: Partial<Omit<LabelOptions, 'showChallenge' | 'showPercentage'>>) {
    if ('showPercentage' in opts)
      throw Error('Use setShowPercentage to change showPercentage instead.')

    if ('showLabel' in opts) throw Error('Use setShowLabel to change showLabel instead.')

    Object.assign(this.labelOptions, opts)
  }

  private tweenValues<T extends keyof LabelOptions>(
    stateKey: LabelOptions[T] extends boolean ? T : never,
    tweenProgressKey: keyof LabelTween,
    visible: boolean,
    animOption: AnimationOption = {}
  ) {
    const { animated } = defaultAnimation(animOption)

    //@ts-ignore
    this.labelOptions[stateKey] = visible

    const perc = visible ? 1 : 0
    const duration = animated ? DURATION : 0

    gsap.to(this.labelTween, {
      [tweenProgressKey]: perc,
      duration,
      onComplete: this.labelOptions.stopLoop,
      onStart: this.labelOptions.startLoop,
    })
  }

  /**
   * Update showChallenge value
   */
  setShowChallenge(showLabel: boolean, animOptions?: AnimationOption) {
    this.tweenValues('showChallenge', 'challengePerc', showLabel, animOptions)
  }

  /**
   * Toggle showChallenge value
   */
  toggleShowChallenge(animOptions?: AnimationOption) {
    this.setShowChallenge(!this.labelOptions.showChallenge, animOptions)
  }

  /**
   * Update showPercentage value
   */
  setShowPercentage(showPercentage: boolean, animOptions?: AnimationOption) {
    this.tweenValues('showPercentage', 'percentagePerc', showPercentage, animOptions)
  }
  /**
   * Toggle showPercentage value
   */
  toggleShowPercentage(animOptions?: AnimationOption) {
    this.setShowPercentage(!this.labelOptions.showPercentage, animOptions)
  }

  /**
   * Draw the label based on current state. To change state call `update`
   */
  draw() {
    return drawChallengeLabel(this.ctx, this.labelOptions, this.labelTween)
  }

  /**
   * Return whether point is inside the label bounding box on not.
   */
  isInside(point: Point) {
    const { left, width, top, height } = computeChallengeLabelMetrics(
      this.ctx,
      this.labelOptions,
      this.labelTween
    )

    return isBetween([left, left + width], point.x) && isBetween([top, top + height], point.y)
  }
}

const computeChallengeLabelMetrics = (
  ctx: CanvasRenderingContext2D,
  opts: Pick<
    LabelOptions,
    'x' | 'y' | 'percentage' | 'align' | 'challenge' | 'fontSize' | 'postfix'
  >,
  tween: Required<LabelTween>
) => {
  const percentageText = formatLabelPercentage(opts.percentage) + opts.postfix
  const challengeText = CHALLENGES_TITLES[opts.challenge]

  const challengeWidth =
    (computeTextWidth(ctx, challengeText, challengeFont(opts.fontSize)) + hSpacing) *
    tween.challengePerc
  const percentageWidth =
    (computeTextWidth(ctx, percentageText, percentageFont(opts.fontSize)) + hSpacing) *
    tween.percentagePerc

  const width = padding.left + iconDimension + challengeWidth + percentageWidth + padding.right

  const [offsetX, offsetY] = computeAlignOffset(width, boxHeight, opts.align)
  const left = opts.x + offsetX
  const top = opts.y - boxHeight + offsetY

  return {
    width,
    height: boxHeight,
    offsetX,
    offsetY,
    left,
    top,
    challengeWidth,
    percentageWidth,
  }
}
